"use client";

import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, XCircle, Info, X } from "lucide-react";
import { cn } from "@/utils/cn";

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

const TOAST_STYLES = {
  success: {
    icon: CheckCircle2,
    classes: "bg-emerald-600/10 border-emerald-500/30",
    iconClass: "text-emerald-400",
  },
  error: {
    icon: XCircle,
    classes: "bg-rose-600/10 border-rose-500/30",
    iconClass: "text-rose-400",
  },
  info: {
    icon: Info,
    classes: "bg-indigo-600/10 border-indigo-500/30",
    iconClass: "text-indigo-400",
  },
};

const DEFAULT_DURATION = 5000;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((type, message, opts = {}) => {
    const id = ++idRef.current;
    const duration = opts.duration ?? DEFAULT_DURATION;
    setToasts((prev) => [...prev, { id, type, message }]);
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  // Memoized so useToast() returns a stable reference across renders —
  // otherwise every ToastProvider re-render (i.e. every toast shown/hidden
  // anywhere in the app) would hand every consumer a new object, forcing
  // "toast" into every dependent useEffect/useCallback's deps array.
  const toast = useMemo(() => ({
    success: (message, opts) => push("success", message, opts),
    error: (message, opts) => push("error", message, opts),
    info: (message, opts) => push("info", message, opts),
    dismiss,
  }), [push, dismiss]);

  return (
    <ToastContext.Provider value={toast}>
      {children}

      {/* Stacked toasts — reuses the visual language of Custom.jsx's lobby
          join/leave notification (spring-in, color-coded border/bg,
          backdrop-blur) so this reads as one consistent system, not a
          second, differently-styled notification mechanism. */}
      <div className="fixed bottom-10 right-10 z-[200] flex flex-col-reverse gap-3 pointer-events-none">
        <AnimatePresence>
          {toasts.map((t) => {
            const style = TOAST_STYLES[t.type] || TOAST_STYLES.info;
            const Icon = style.icon;
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, y: 50, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: 100, scale: 0.8 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="pointer-events-auto"
              >
                <div
                  className={cn(
                    "flex items-center gap-3 py-3 pl-4 pr-3 rounded-lg border text-white backdrop-blur-xl shadow-2xl shadow-black/50 max-w-sm",
                    style.classes
                  )}
                >
                  <Icon size={16} className={cn(style.iconClass, "flex-shrink-0")} />
                  <p className="text-sm font-semibold text-zinc-200 flex-1">{t.message}</p>
                  <button
                    onClick={() => dismiss(t.id)}
                    className="text-zinc-500 hover:text-zinc-300 transition-colors flex-shrink-0"
                    aria-label="Dismiss notification"
                  >
                    <X size={14} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};
